import { useState, useEffect, createContext, useContext, useMemo } from "react";
import { User, Session } from '@supabase/supabase-js';
import { supabase } from "@/integrations/supabase/client";
import { SystemUserRole, SYSTEM_ROLES, SYSTEM_ROLE_MAP } from "@/schemas/systemUserRole";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
  role: SystemUserRole | null;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Get role from user metadata
  const role = useMemo<SystemUserRole | null>(() => {
    if (!user) return null;

    const userMeta = user.user_metadata || {};
    const roleValue = userMeta.role;

    // Convert role string to SystemUserRole
    if (roleValue === 'admin' || roleValue === 'ADMIN') {
      return SYSTEM_ROLES.ADMIN;
    } else if (roleValue === 'user' || roleValue === 'USER') {
      return SYSTEM_ROLES.USER;
    }
    
    // Default to USER if role exists but doesn't match
    return roleValue ? SYSTEM_ROLES.USER : null;
  }, [user]);

  // Check if user is admin
  const isAdmin = useMemo(() => role === SYSTEM_ROLES.ADMIN, [role]);

  const value = {
    user,
    session,
    loading,
    signOut,
    role,
    isAdmin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};